import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, InjectionToken, Injector, Type, createComponent, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Data passed to a dialog component (replacement for MAT_DIALOG_DATA). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DIALOG_DATA = new InjectionToken<any>('DIALOG_DATA');

export interface DialogConfig<D = unknown> {
  data?: D;
  /** CSS width of the dialog box (e.g. '700px', 'min(820px, 95vw)'). */
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  /** Prevent closing on Escape / backdrop click. */
  disableClose?: boolean;
  /** Accepted for MatDialog API compatibility; the native dialog focuses its first control. */
  autoFocus?: boolean | string;
  panelClass?: string | string[];
}

/** Handle to an open dialog (replacement for MatDialogRef). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class DialogRef<T = unknown, R = any> {
  private readonly closed$ = new Subject<R | undefined>();
  private done = false;
  componentInstance!: T;

  constructor(private readonly dialogEl: HTMLElement & { close(value?: string): void }, private readonly teardown: () => void) {}

  /** Close with an optional result, delivered to `afterClosed()`. */
  close(result?: R): void {
    if (this.done) return;
    this.done = true;
    this.closed$.next(result);
    this.closed$.complete();
    this.dialogEl.close();
    this.teardown();
  }

  /** Called when the native dialog closed itself (Escape / backdrop). */
  closedByUser(): void {
    this.close(undefined);
  }

  afterClosed(): Observable<R | undefined> {
    return this.closed$.asObservable();
  }
}

/**
 * Opens a component inside the OneVedAI UI dialog (native <dialog>: focus trap, Escape, top layer).
 * Drop-in for MatDialog.open(): `open(Component, { data, width })` → DialogRef.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly injector = inject(Injector);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  open<T, D = unknown, R = any>(component: Type<T>, config: DialogConfig<D> = {}): DialogRef<T, R> {
    const dialogEl = document.createElement('onevedai-dialog') as HTMLElement & { close(value?: string): void; open: boolean; lightDismiss: boolean };
    dialogEl.setAttribute('no-header', '');
    dialogEl.setAttribute('no-footer', '');
    dialogEl.classList.add('app-dialog');
    (Array.isArray(config.panelClass) ? config.panelClass : config.panelClass ? [config.panelClass] : []).forEach((c) => dialogEl.classList.add(c));
    if (config.width) dialogEl.style.setProperty('--dialog-width', config.width);
    if (config.maxWidth) dialogEl.style.setProperty('--dialog-max-width', config.maxWidth);
    if (config.maxHeight) dialogEl.style.setProperty('--dialog-max-height', config.maxHeight);
    dialogEl.lightDismiss = !config.disableClose;
    document.body.appendChild(dialogEl);

    let componentRef: ComponentRef<T> | null = null;
    const teardown = () => {
      // let the native close animation/event finish before removing the DOM
      queueMicrotask(() => {
        componentRef?.destroy();
        componentRef = null;
        dialogEl.remove();
      });
    };
    const ref = new DialogRef<T, R>(dialogEl, teardown);

    const elementInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DIALOG_DATA, useValue: config.data },
        { provide: DialogRef, useValue: ref },
      ],
    });
    componentRef = createComponent(component, { environmentInjector: this.envInjector, elementInjector });
    ref.componentInstance = componentRef.instance;
    this.appRef.attachView(componentRef.hostView);
    dialogEl.appendChild(componentRef.location.nativeElement);

    dialogEl.addEventListener('ov-close', () => ref.closedByUser());
    if (config.disableClose) {
      // keep the dialog open on Escape
      dialogEl.addEventListener('cancel', (e) => e.preventDefault(), true);
    }
    // `open` must be set after the element upgrades and renders
    requestAnimationFrame(() => (dialogEl.open = true));
    return ref;
  }
}
